import { Edit, ExpandLess, ExpandMore, Save } from '@mui/icons-material';
import {
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import { formatDate, showAlert } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  createAccountingEntriesBatch,
  createAccountingEntry,
  editAccountingEntry,
  getAccountingEntryClasses,
  getAccountingEntryTypes,
  getAccountingNodes,
  getAccountingPeriods,
} from '../../../../../../../lib/api/accounting';
import CustomButton from '../../../../../../../lib/designSystem/Button';
import ColoredCircle from '../../../../../../../lib/designSystem/ColoredCircle';
import {
  AccountingEntryClass,
  AccountingNode,
  AccountingPeriod,
  EntryType,
} from '../../../types';
import EntryWithLines from '../../AccountingEntries/components/EntryWithLines/EditEntry';
import EntryModal from './Modal';
export interface EntryLine {
  entryId: string;
  className: string;
  account: string;
  accountAuxiliaryCode: string;
  accountCodeType: string;
  accountName: string;
  businessIdentifier: string;
  businessName: string;
  category: string;
  credit: number;
  date: string;
  debit: number;
  description: string;
  documentId: string;
  isBalanced: boolean;
  movementKey: string;
  nodeId: number;
  type: string;
  id?: string | null;
  costCenterId?: string | null;
  documentKey?: string;
}

export interface Entry {
  businessIdentifier: string;
  businessName: string;
  category: string;
  checkSum: string;
  groupedLines: EntryLine[];
  isBalanced: boolean;
  isEdited: boolean;
  sourceKey: string;
}

export interface DayEntry {
  date: string;
  groupedLinesByCategoryAndBusiness: Entry[];
}

export interface StatusIndicator {
  color: string;
  label: string;
}

export interface EntryForEditing {
  id: string | null;
  name: string;
  typeId: string;
  classId: string;
  date: string;
  sourceKey: string;
  lines: EntryLine[];
}
const useStyles = makeStyles(() =>
  createStyles({
    table: {
      width: '100%',
    },
    tableCell: {
      padding: '8px !important',
      overflow: 'hidden',
      minWidth: '50px',
      maxWidth: '180px',
      whiteSpace: 'nowrap',
      fontSize: '14px !important', // Reduced font size
    },
    tableCellTasa: {
      padding: '8px !important',
      overflow: 'hidden',
      maxWidth: '50px',
      whiteSpace: 'nowrap',
      fontSize: '14px !important', // Reduced font size
      textAlign: 'right',
    },
    tableCellNames: {
      padding: '8px !important',
      overflow: 'hidden',
      maxWidth: '80px',
      whiteSpace: 'nowrap',
      fontSize: '14px !important', // Reduced font size
    },
    tableContainer: {
      border: '1px solid #e0e0e0',
      borderRadius: '10px',
    },
    tableBody: {
      backgroundColor: '#ffffff',
    },
    textField: {
      fontSize: '14px !important',
      padding: '8px !important',
    },
    textFieldAutocomplete: {
      fontSize: '14px !important',
      padding: '0px !important',
      minWidth: '120px !important',
    },
    iconButton: {
      padding: '5px',
    },
    clickableRow: {
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: '#f5f5f5',
      },
    },
    greenRow: {
      backgroundColor: '#e8f5e8',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: '#d4edda',
      },
    },
    redRow: {
      backgroundColor: '#ffeaea',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: '#f8d7da',
      },
    },
    yellowRow: {
      backgroundColor: '#fff3cd',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: '#ffeaa7',
      },
    },
  }),
);

interface DailyBookEntriesTableProps {
  entries: DayEntry[];
  refreshData?: (showLoading?: boolean) => Promise<void>;
  onDataUpdate?: (updatedEntries: DayEntry[]) => void;
  onDataRevert?: (originalEntries: DayEntry[]) => void;
  allowEditing?: boolean;
  isTransactional?: boolean;
}

const BookEntriesTable = (props: DailyBookEntriesTableProps) => {
  const { entries, allowEditing = true, isTransactional = false } = props;
  const dispatch = useDispatch();

  const { mutateAsync: createEntryMutation } = createAccountingEntry(dispatch);
  const { mutateAsync: editEntryMutation } = editAccountingEntry(dispatch);

  const [expandedDays, setExpandedDays] = useState<Record<number, boolean>>({});
  const classes = useStyles();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<any>(null);
  const [entryWithLinesOpen, setEntryWithLinesOpen] = useState(false);
  const [entryForEditing, setEntryForEditing] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertText, setAlertText] = useState('');
  const [alertSeverity, setAlertSeverity] = useState<'success' | 'error'>(
    'error',
  );
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [dayToSave, setDayToSave] = useState<any>(null);
  const [dayIndexToSave, setDayIndexToSave] = useState<number>(-1);

  const [entryTypes, setEntryTypes] = useState<EntryType[]>([]);
  const [nodes, setNodes] = useState<AccountingNode[]>([]);
  const [entryClasses, setEntryClasses] = useState<AccountingEntryClass[]>([]);
  const [accountingPeriods, setAccountingPeriods] = useState<
    AccountingPeriod[]
  >([]);

  const setUpEntryEditing = async () => {
    try {
      const response = await getAccountingEntryTypes(dispatch);
      const responseNodes = await getAccountingNodes(dispatch);
      const responseEntryClasses = await getAccountingEntryClasses(dispatch);
      const responsePeriods = await getAccountingPeriods(dispatch);

      setEntryTypes(response);
      setNodes(responseNodes);
      setEntryClasses(responseEntryClasses);
      setAccountingPeriods(responsePeriods);
    } catch (error) {
      console.error('Error fetching entry editing data:', error);
    }
  };

  const handleSetSuccessText = (text: string | null) => {
    if (text) {
      setAlertText(text);
      setAlertSeverity('success');
      setAlertOpen(true);
    }
  };

  const handleSetFailedText = (text: string | null) => {
    if (text) {
      setAlertText(text);
      setAlertSeverity('error');
      setAlertOpen(true);
    }
  };

  const handleAlertClose = (open: boolean) => {
    setAlertOpen(open);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedEntry(null);
  };

  const handleCloseConfirmModal = () => {
    setConfirmModalOpen(false);
    setDayToSave(null);
    setDayIndexToSave(-1);
  };

  const handleConfirmSave = () => {
    if (dayToSave && dayIndexToSave >= 0) {
      void handleSaveDay(dayToSave, dayIndexToSave);
      handleCloseConfirmModal();
    }
  };

  const handleDailyCloseClick = (day: any, dayIndex: number) => {
    setDayToSave(day);
    setDayIndexToSave(dayIndex);
    setConfirmModalOpen(true);
  };

  const handleEditEntry = (entry: any) => {
    const entryForEdit = {
      id: entry.groupedLines[0].entryId || null,
      name: entry.name || entry.category || '',
      typeId: entry.typeId,
      classId: entry.classId,
      date: entry.date || new Date().toISOString().slice(0, 10),
      sourceKey: entry.sourceKey,
      lines:
        entry.groupedLines?.map((line: any) => ({
          id: line.id || null,
          nodeId: line.nodeId || null,
          debit: line.debit || 0,
          credit: line.credit || 0,
          costCenterId: line.costCenterId || null,
          documentKey: line.documentKey || '',
          businessIdentifier: line.businessIdentifier || '',
          description: line.description || '',
          node: line.node || null,
        })) || [],
    };

    setEntryForEditing(entryForEdit);
    setEntryWithLinesOpen(true);
  };

  const handleSetUpAfterEdit = async () => {
    // Refresh data silently after editing an entry
    if (props.refreshData) {
      await props.refreshData(false);
    }
  };

  const handleSaveEntry = async (entry: any) => {
    setSaving(true);

    // Store original data for rollback
    const originalEntries = props.entries;

    try {
      // Convert entry data to format for API
      const entryData = {
        name: entry.category,
        businessIdentifier: entry.businessIdentifier,
        businessName: entry.businessName,
        typeId: entry.typeId,
        classId: entry.classId,
        //TODO: Revisar fechas
        date: new Date(entry.groupedLines[0].date),
        lines:
          entry.groupedLines?.map((line: any) => ({
            id: line.id || null, // Include line ID for editing
            nodeId: line.nodeId || null,
            debit: line.debit || 0,
            credit: line.credit || 0,
            costCenterId: line.costCenterId || null,
            documentKey: line.documentId || '',
            businessIdentifier: line.businessIdentifier || '',
            description: line.description || '',
          })) || [],
      };

      entryData.date.setUTCHours(12);

      let response;
      if (entry.sourceKey) {
        response = await editEntryMutation({
          ...entryData,
          id: entry.writtenEntryId,
        });
      } else {
        response = await createEntryMutation(entryData);
      }

      updateLocalDataWithServerResponse(entry, response, entry.date);

      setModalOpen(false);
      setSelectedEntry(null);
      setSaving(false);
    } catch (error) {
      console.error('Error saving accounting entry:', error);
      props.onDataRevert?.(originalEntries);

      // Show error alert
      const action = entry.sourceKey ? 'editar' : 'crear';
      setAlertText(
        `Error al ${action} el asiento contable. Por favor, inténtelo de nuevo.`,
      );
      setAlertSeverity('error');
      setAlertOpen(true);
      setSaving(false);
    }
  };

  const updateLocalDataWithServerResponse = (
    category: Entry,
    serverResponse: any,
    targetDate?: string,
  ) => {
    const updatedEntries = props.entries.map((day: any) => {
      if (targetDate && day.date !== targetDate) {
        return day;
      }

      return {
        ...day,
        groupedLinesByCategoryAndBusiness:
          day.groupedLinesByCategoryAndBusiness.map((cat: Entry) => {
            if (
              cat.businessIdentifier === category.businessIdentifier &&
              cat.category === category.category
            ) {
              // Map server response lines to local format
              const updatedLines =
                serverResponse.lines?.map((line: any, index: number) => ({
                  ...cat.groupedLines[index], // preserve base structure from original line
                  id: line.id,
                  entryId: serverResponse.id,
                  nodeId: line.nodeId,
                  debit: line.debit,
                  credit: line.credit,
                  costCenterId: line.costCenterId,
                  documentKey: line.documentKey,
                  documentId: line.documentKey,
                  description: line.description,
                  businessIdentifier: line.businessIdentifier,
                })) || cat.groupedLines;

              const updatedEntry = {
                ...cat,
                sourceKey:
                  serverResponse.id?.toString() || serverResponse.sourceKey,
                writtenEntryId: serverResponse.id,
                isEdited: false,
                groupedLines: updatedLines,
              };
              return updatedEntry;
            }
            return cat;
          }),
      };
    });

    props.onDataUpdate?.(updatedEntries);
  };

  const updateMultipleCategories = (
    categories: any[],
    isSaved: boolean,
    targetDate: string,
  ) => {
    const updatedEntries = props.entries.map((day: any) => {
      if (day.date !== targetDate) {
        return day;
      }

      const updatedCategories = day.groupedLinesByCategoryAndBusiness.map(
        (cat: Entry) => {
          const shouldUpdate = categories.some(
            (category) =>
              cat.businessIdentifier === category.businessIdentifier &&
              cat.category === category.category,
          );

          if (shouldUpdate) {
            return {
              ...cat,
              sourceKey: isSaved ? `saved_${Date.now()}` : null,
              isEdited: false,
            };
          }
          return cat;
        },
      );

      return {
        ...day,
        groupedLinesByCategoryAndBusiness: updatedCategories,
      };
    });

    props.onDataUpdate?.(updatedEntries);
  };

  const toggleDay = (dayIndex: number) => {
    setExpandedDays((prev) => ({ ...prev, [dayIndex]: !prev[dayIndex] }));
  };

  const handleRowClick = (row: any) => {
    setSelectedEntry(row);
    setModalOpen(true);
  };

  const handleSaveDay = async (day: any, _dayIndex: number) => {
    setSaving(true);
    const originalEntries = props.entries;

    try {
      const categories = day.groupedLinesByCategoryAndBusiness || [];
      const unsavedCategories = categories.filter(
        (cat: Entry) => !cat.sourceKey,
      );

      if (unsavedCategories.length === 0) {
        showAlert(
          dispatch,
          'error',
          'No hay asientos pendientes de guardar para este día.',
        );
        setSaving(false);
        return;
      }

      const batchRequest = {
        startDate: day.date,
        endDate: day.date,
      };

      const response = await createAccountingEntriesBatch(
        dispatch,
        batchRequest,
      );
      if (response.length === 0) {
        showAlert(dispatch, 'error', 'No hay períodos abiertos para este día.');
        setSaving(false);
        return;
      }
      updateMultipleCategories(unsavedCategories, true, day.date);

      showAlert(
        dispatch,
        'success',
        `Se guardaron ${unsavedCategories.length} asientos para el día ${day.date}.`,
      );
      setSaving(false);
      if (props.refreshData) {
        await props.refreshData(false);
      }
    } catch (error) {
      console.error('Error saving day entries:', error);
      props.onDataRevert?.(originalEntries);
      showAlert(
        dispatch,
        'error',
        'Error al guardar los asientos del día. Por favor, inténtelo de nuevo.',
      );
      setSaving(false);
    }
  };

  const getBalanceIndicator = (category: Entry) => {
    if (category.isBalanced) {
      return { color: '#28a745', label: 'Balanceado' }; // Green
    }

    return { color: '#dc3545', label: 'Desbalanceado' }; // Red
  };
  const getStatusIndicator = (category: Entry) => {
    if (!category.sourceKey) {
      return { color: '#dc3545', label: 'Sin guardar' }; // Red
    }
    if (category.isEdited) {
      return { color: '#ffc107', label: 'Con cambios' }; // Yellow
    }

    return { color: '#28a745', label: 'Guardado' }; // Green
  };

  const getDayStatusIndicator = (day: any) => {
    const categories = day.groupedLinesByCategoryAndBusiness || [];

    if (categories.length === 0) {
      return { color: '#6c757d', label: 'Sin datos' }; // Gray
    }

    const hasUnsaved = categories.some((cat: Entry) => !cat.sourceKey);
    const hasEdited = categories.some((cat: Entry) => cat.isEdited);
    const allSaved = categories.every(
      (cat: Entry) => cat.sourceKey && !cat.isEdited,
    );

    if (hasUnsaved) {
      return { color: '#dc3545', label: 'Pendiente' }; // Red - has unsaved entries
    }
    if (hasEdited) {
      return { color: '#ffc107', label: 'Con cambios' }; // Yellow - has edited entries
    }
    if (allSaved) {
      return { color: '#28a745', label: 'Completado' }; // Green - all saved
    }

    return { color: '#6c757d', label: 'Mixto' }; // Gray - mixed status
  };

  const hasUnsavedEntries = (day: any) => {
    const categories = day.groupedLinesByCategoryAndBusiness || [];
    return categories.some((cat: Entry) => !cat.sourceKey || cat.isEdited);
  };

  useEffect(() => {
    void setUpEntryEditing();
  }, []);

  return (
    <div style={{ paddingBottom: 100 }}>
      <TableContainer className={classes.tableContainer}>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell>Fecha</TableCell>
              {isTransactional && <TableCell>Estado</TableCell>}
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody className={classes.tableBody}>
            {entries.map((day: any, dayIndex: number) => (
              <React.Fragment key={dayIndex}>
                {/* Nivel 1: Día */}
                <TableRow
                  hover
                  className={classes.clickableRow}
                  onClick={() => toggleDay(dayIndex)}
                  style={{
                    cursor: 'pointer',
                    backgroundColor: expandedDays[dayIndex]
                      ? '#f5f5f5'
                      : undefined,
                  }}
                >
                  <TableCell className={classes.tableCell}>
                    {formatDate(day.date, 'dd-mm-yyyy')}
                  </TableCell>
                  {isTransactional && (
                    <>
                      <TableCell className={classes.tableCell}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                          }}
                        >
                          <ColoredCircle
                            color={getDayStatusIndicator(day).color}
                            size={12}
                          />
                          <span style={{ fontSize: '12px' }}>
                            {getDayStatusIndicator(day).label}
                          </span>
                        </div>
                      </TableCell>
                    </>
                  )}
                  <TableCell
                    className={classes.tableCell}
                    style={{ textAlign: 'right' }}
                  >
                    {expandedDays[dayIndex] ? <ExpandLess /> : <ExpandMore />}
                  </TableCell>
                </TableRow>

                {expandedDays[dayIndex] && (
                  <TableRow>
                    <TableCell colSpan={3} style={{ padding: 0 }}>
                      <Collapse
                        in={expandedDays[dayIndex]}
                        timeout="auto"
                        unmountOnExit
                      >
                        <Paper
                          elevation={0}
                          style={{ padding: 16, margin: '12px 0' }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              marginBottom: 8,
                            }}
                          >
                            <Typography
                              variant="subtitle1"
                              style={{ fontWeight: 600 }}
                            >
                              Asientos {day.date}
                            </Typography>
                            {isTransactional && hasUnsavedEntries(day) && (
                              <CustomButton
                                size="small"
                                color="success"
                                startIcon={<Save />}
                                onClick={() =>
                                  handleDailyCloseClick(day, dayIndex)
                                }
                                disabled={saving}
                                loading={saving}
                                style={{
                                  fontSize: '12px',
                                  padding: '6px 12px',
                                  minWidth: 'auto',
                                }}
                              >
                                Cierre Diario
                              </CustomButton>
                            )}
                          </div>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                {!isTransactional && (
                                  <TableCell className={classes.tableCell}>
                                    ID
                                  </TableCell>
                                )}
                                <TableCell className={classes.tableCell}>
                                  Clase
                                </TableCell>
                                <TableCell className={classes.tableCell}>
                                  Rut
                                </TableCell>

                                <TableCell className={classes.tableCell}>
                                  Empresa
                                </TableCell>
                                <TableCell className={classes.tableCell}>
                                  Asiento
                                </TableCell>
                                {isTransactional && (
                                  <>
                                    <TableCell className={classes.tableCell}>
                                      Estado
                                    </TableCell>
                                    <TableCell className={classes.tableCell}>
                                      Balance
                                    </TableCell>
                                  </>
                                )}
                                <TableCell></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {day.groupedLinesByCategoryAndBusiness.map(
                                (category: Entry, catIndex: number) => (
                                  <React.Fragment
                                    key={`${dayIndex}-${catIndex}`}
                                  >
                                    <TableRow
                                      className={classes.clickableRow}
                                      onClick={() =>
                                        handleRowClick({
                                          ...category,
                                          date: day.date,
                                        })
                                      }
                                    >
                                      {!isTransactional && (
                                        <TableCell
                                          className={classes.tableCell}
                                        >
                                          {category.groupedLines[0].entryId}
                                        </TableCell>
                                      )}
                                      <TableCell className={classes.tableCell}>
                                        {category.groupedLines[0].className}
                                      </TableCell>
                                      <TableCell className={classes.tableCell}>
                                        {category.businessIdentifier}
                                      </TableCell>
                                      <TableCell className={classes.tableCell}>
                                        {category.businessName}
                                      </TableCell>
                                      <TableCell className={classes.tableCell}>
                                        {category.category}
                                      </TableCell>
                                      {isTransactional && (
                                        <>
                                          <TableCell
                                            className={classes.tableCell}
                                          >
                                            <div
                                              style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                              }}
                                            >
                                              <ColoredCircle
                                                color={
                                                  getStatusIndicator(category)
                                                    .color
                                                }
                                                size={12}
                                              />
                                              <span
                                                style={{ fontSize: '12px' }}
                                              >
                                                {
                                                  getStatusIndicator(category)
                                                    .label
                                                }
                                              </span>
                                            </div>
                                          </TableCell>
                                          <TableCell
                                            className={classes.tableCell}
                                          >
                                            <div
                                              style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                              }}
                                            >
                                              <ColoredCircle
                                                color={
                                                  getBalanceIndicator(category)
                                                    .color
                                                }
                                                size={12}
                                              />
                                              <span
                                                style={{ fontSize: '12px' }}
                                              >
                                                {
                                                  getBalanceIndicator(category)
                                                    .label
                                                }
                                              </span>
                                            </div>
                                          </TableCell>
                                        </>
                                      )}
                                      <TableCell
                                        className={classes.tableCell}
                                        style={{ textAlign: 'right' }}
                                      >
                                        {allowEditing && (
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              handleRowClick({
                                                ...category,
                                                date: day.date,
                                              })
                                            }
                                            style={{ padding: '4px' }}
                                          >
                                            <Edit fontSize="small" />
                                          </IconButton>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  </React.Fragment>
                                ),
                              )}
                            </TableBody>
                          </Table>
                        </Paper>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
        <EntryModal
          open={modalOpen}
          onClose={handleCloseModal}
          entry={selectedEntry}
          onEdit={handleEditEntry}
          onSave={isTransactional ? handleSaveEntry : undefined}
          saving={saving}
          readOnly={!allowEditing}
          allowSaving={isTransactional}
        />
        {entryWithLinesOpen && entryForEditing && (
          <EntryWithLines
            entry={entryForEditing}
            setEntry={(updatedEntry: any) => {
              setEntryForEditing(updatedEntry);
            }}
            open={entryWithLinesOpen}
            setOpen={(open: boolean) => {
              setEntryWithLinesOpen(open);
            }}
            setUp={handleSetUpAfterEdit}
            setSuccessText={handleSetSuccessText}
            setFailedText={handleSetFailedText}
            entryTypes={entryTypes}
            nodes={nodes}
            entryClasses={entryClasses}
            accountingPeriods={accountingPeriods}
          />
        )}
        <Dialog
          open={confirmModalOpen}
          onClose={handleCloseConfirmModal}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Confirmar Cierre Diario</DialogTitle>
          <DialogContent>
            <Typography variant="body1" style={{ marginBottom: 16 }}>
              ¿Estás seguro que quieres realizar el cierre diario para el día{' '}
              <strong>{dayToSave?.date}</strong>?
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Esta acción guardará todos los asientos pendientes para este día.
            </Typography>
          </DialogContent>
          <DialogActions>
            <CustomButton
              onClick={handleCloseConfirmModal}
              color="secondary"
              disabled={saving}
            >
              Cancelar
            </CustomButton>
            <CustomButton
              onClick={handleConfirmSave}
              color="success"
              disabled={saving}
              loading={saving}
            >
              Confirmar Cierre
            </CustomButton>
          </DialogActions>
        </Dialog>
      </TableContainer>
    </div>
  );
};

export default BookEntriesTable;
